import { useState } from "react";
import { useGetCompanyPreview } from "@/hooks/api/query/useGetCompanyPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { Nis2ResultEnum } from "@/api/types/companyPreview";

const RESULT_STYLES: Record<
  Nis2ResultEnum,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
  }
> = {
  ESSENTIAL: { label: "ESENȚIAL", variant: "destructive" },
  IMPORTANT: { label: "IMPORTANT", variant: "secondary" },
  IMPACT: { label: "IMPACT (Art. 9)", variant: "outline" },
  OUT_OF_SCOPE: { label: "ÎN AFARA SCOPULUI", variant: "default" },
};

export default function DebugCompanyPreview() {
  const [cuiInput, setCuiInput] = useState("");
  const [submittedCui, setSubmittedCui] = useState<string>();

  const { data, isLoading, error, isFetching } =
    useGetCompanyPreview(submittedCui);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = cuiInput.trim();
    if (trimmed) {
      setSubmittedCui(trimmed);
    }
  };

  const classification = data?.nis2Classification;
  const classificationStyle = classification
    ? RESULT_STYLES[classification.classification]
    : null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Company Preview - NIS2 Check</h1>
          <Badge variant="outline" className="text-xs">
            DEBUG PAGE
          </Badge>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Verificare CUI</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="flex gap-3">
              <Input
                placeholder="Introduceți CUI (ex: 12345678)"
                value={cuiInput}
                onChange={(e) => setCuiInput(e.target.value)}
                className="max-w-xs"
              />
              <Button type="submit" disabled={isLoading || !cuiInput.trim()}>
                {isLoading ? "Se caută..." : "Verifică"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {error && (
          <Alert variant="destructive">
            <AlertTitle>Eroare</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : "Eroare necunoscută"}
            </AlertDescription>
          </Alert>
        )}

        {isFetching && !data && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Se încarcă datele...
            </CardContent>
          </Card>
        )}

        {data && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  Companie
                  {classificationStyle && (
                    <Badge variant={classificationStyle.variant}>
                      {classificationStyle.label}
                    </Badge>
                  )}
                  {!classification && data.size.value === null && (
                    <Badge variant="outline">
                      Date financiare indisponibile
                    </Badge>
                  )}
                  {isFetching && (
                    <Badge variant="outline" className="text-xs">
                      refreshing...
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <InfoRow label="Denumire" value={data.company.name} />
                <InfoRow label="CUI" value={data.company.cui} />
                <InfoRow label="CIF" value={data.company.cif} />
                <InfoRow
                  label="CAEN Principal"
                  value={
                    data.company.caenPrincipal
                      ? `${data.company.caenPrincipal.code} — ${data.company.caenPrincipal.description}`
                      : null
                  }
                />
                <InfoRow
                  label="Coduri CAEN autorizate"
                  value={
                    data.company.authorizedCaenCodes.length > 0
                      ? data.company.authorizedCaenCodes
                          .map((c) => `${c.code} (v${c.version})`)
                          .join(", ")
                      : "—"
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Dimensiune</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <InfoRow
                  label="Categorie"
                  value={data.size.value ?? "Indisponibil"}
                />
                <InfoRow label="Explicație" value={data.size.explanation} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  Clasificare NIS2
                  {classificationStyle && (
                    <Badge variant={classificationStyle.variant}>
                      {classificationStyle.label}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!classification ? (
                  <p className="text-muted-foreground">
                    Clasificarea nu este disponibilă (lipsesc datele financiare
                    pentru determinarea dimensiunii).
                  </p>
                ) : classification.evaluations.length === 0 ? (
                  <p className="text-muted-foreground">
                    Niciun serviciu NIS2 evaluat.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {classification.evaluations.map((ev, i) => {
                      const style = RESULT_STYLES[ev.result];
                      return (
                        <div
                          key={i}
                          className="flex items-start gap-3 rounded-md border p-3"
                        >
                          <Badge
                            variant={style.variant}
                            className="mt-0.5 shrink-0"
                          >
                            {style.label}
                          </Badge>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              {ev.serviceId && (
                                <span className="font-mono text-xs text-muted-foreground">
                                  {ev.serviceId}
                                </span>
                              )}
                              {ev.type && (
                                <span className="text-xs text-muted-foreground">
                                  (tip {ev.type})
                                </span>
                              )}
                            </div>
                            <p className="font-medium">{ev.description}</p>
                            <p className="text-muted-foreground">{ev.reason}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  Sectoare identificate ({data.matchedSectors.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.matchedSectors.length === 0 ? (
                  <p className="text-muted-foreground">
                    Niciun sector NIS2 identificat cu scor de incredere peste
                    70%.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {data.matchedSectors.map((sector) => {
                      const sorted = [...sector.services].sort(
                        (a, b) => b.scorIncredere - a.scorIncredere
                      );
                      const top5 = sorted.slice(0, 5);
                      const remaining = sorted.length - top5.length;

                      return (
                        <div
                          key={sector.sectorId}
                          className="rounded-md border p-4 space-y-2"
                        >
                          <div className="space-y-1">
                            <span className="font-medium block">
                              {sector.sectorName}
                            </span>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{sector.annex}</Badge>
                              <Badge variant="secondary">
                                {sector.serviceCount} servicii
                              </Badge>
                            </div>
                          </div>
                          <div className="ml-4 space-y-1">
                            {top5.map((svc) => (
                              <div
                                key={svc.entityId}
                                className="flex items-center gap-2 text-sm"
                              >
                                <span className="text-muted-foreground font-mono text-xs">
                                  {svc.entityId}
                                </span>
                                <span>{svc.name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {svc.scorIncredere}%
                                </Badge>
                              </div>
                            ))}
                            {remaining > 0 && (
                              <p className="text-sm text-muted-foreground mt-1">
                                + {remaining} alte servicii
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Raw JSON</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="max-h-96 overflow-auto rounded-md bg-gray-900 p-4 text-xs text-gray-100">
                  {JSON.stringify(data, null, 2)}
                </pre>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground min-w-[140px] shrink-0">
        {label}:
      </span>
      <span className="font-medium">{value ?? "—"}</span>
    </div>
  );
}
